'use client';

/**
 * ArchDetailsCard — selected-arch-entity details, as a composition card.
 *
 * Copy of components/copilot/insights/arch-details-panel.tsx, adapted for
 * the card registry (content_key `arch_details_canvas`). Reads
 * `useGraphSelectionStore.selectedNode` — so when a node is clicked in
 * the capability-map / topology cards, this shows its label/type/props
 * (+ linked tests). Original stays for the legacy `docs` copilot until
 * that mode is retired (cp-and-reuse).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain docs
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Layers,
  Zap,
  FileText,
  BookOpen,
  FolderTree,
  FlaskConical,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { SectionHeader } from '@/components/copilot/details/shared';
import { cn } from '@/lib/utils/cn';

// Type badge colors - consistent with graph node colors
const TYPE_BADGES: Record<string, string> = {
  system: 'bg-purple-500/20 text-purple-500',
  package: 'bg-blue-500/20 text-blue-500',
  capability: 'bg-amber-500/20 text-amber-500',
  feature: 'bg-green-500/20 text-green-500',
  story: 'bg-gray-500/20 text-gray-400',
  test_case: 'bg-red-500/20 text-red-500',
};

// Icon component for entity types
function TypeIcon({ type, className }: { type: string; className?: string }) {
  switch (type) {
    case 'system':
      return <Box className={className} />;
    case 'package':
      return <Layers className={className} />;
    case 'capability':
      return <Zap className={className} />;
    case 'feature':
      return <FileText className={className} />;
    case 'story':
      return <BookOpen className={className} />;
    case 'test_case':
      return <FlaskConical className={className} />;
    default:
      return <FileText className={className} />;
  }
}

export function ArchDetailsCard() {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);
  const testDetailStrategy = useCapabilityStore((s) => s.strategyName('testing', 'detail'));

  // All hooks above any early return — `selectedNode` may be null on first
  // render and populated later, so the test-detail enrichment lives here
  // (guarded) rather than after the empty-state return.
  const [testData, setTestData] = useState<Record<string, unknown> | null>(null);
  const nodeId = selectedNode?.id;
  const nodeType = selectedNode?.type;
  useEffect(() => {
    if (nodeType !== 'test_case' || !nodeId) { setTestData(null); return; }
    api.getStrategyData(testDetailStrategy ?? 'test_details', { entity_id: nodeId }).then((res: any) => {
      setTestData(res?.data?.data ?? null);
    }).catch(() => setTestData(null));
  }, [nodeType, nodeId, testDetailStrategy]);

  // Empty state
  if (!selectedNode) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
        <FolderTree className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm font-medium">Architecture Details</p>
        <p className="text-xs mt-1">Select an item from the tree or graph</p>
      </div>
    );
  }

  const { id, label, type, properties } = selectedNode;

  // Test case renders through its own section below
  if (type === 'test_case') {
    return <TestCaseDetailsView id={id} label={label} properties={properties} data={testData} />;
  }

  const description = properties?.description as string | undefined;
  const childCount = properties?.child_count as number | undefined;
  const semanticId = properties?.semanticId as string | undefined;

  // Parent info (for capabilities, features, stories)
  const parentName = properties?.parent_name as string | undefined;
  const parentSemanticId = properties?.parent_semantic_id as string | undefined;

  // Package-specific: child capabilities (for Flow+ view)
  const capabilities = properties?.capabilities as Array<{
    name: string;
    semantic_id: string;
    description?: string;
  }> | undefined;

  // Story-specific properties
  const asA = properties?.as_a as string | undefined;
  const iWant = properties?.i_want as string | undefined;
  const soThat = properties?.so_that as string | undefined;
  const acceptanceCriteria = properties?.acceptance_criteria as string | undefined;

  return (
    <div className="p-3 space-y-4">
      {/* Header with type badge */}
      <div className="flex items-start gap-3">
        <div className={cn(
          'p-2 rounded-lg',
          TYPE_BADGES[type] || TYPE_BADGES.story
        )}>
          <TypeIcon type={type} className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold truncate">{label}</h3>
          <span className={cn(
            'inline-block mt-1 px-2 py-0.5 rounded text-xs capitalize',
            TYPE_BADGES[type] || TYPE_BADGES.story
          )}>
            {type}
          </span>
        </div>
      </div>

      {/* Description Section */}
      {description && (
        <div>
          <SectionHeader title="Description" />
          <div className="p-2 rounded bg-muted/30 border border-border">
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {description}
            </p>
          </div>
        </div>
      )}

      {/* Package-specific: Capabilities Section */}
      {type === 'package' && capabilities && capabilities.length > 0 && (
        <div>
          <SectionHeader title="Capabilities" count={capabilities.length} />
          <div className="space-y-2">
            {capabilities.map((cap) => (
              <div
                key={cap.semantic_id}
                className="p-2 rounded bg-muted/30 border border-border"
              >
                <div className="flex items-center gap-2">
                  <Zap className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                  <p className="text-sm font-medium text-foreground">{cap.name}</p>
                </div>
                {cap.description && (
                  <p className="text-xs text-muted-foreground mt-1 ml-5.5 line-clamp-2">
                    {cap.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Story-specific: User Story Format */}
      {type === 'story' && (asA || iWant || soThat) && (
        <div>
          <SectionHeader title="User Story" />
          <div className="p-2 rounded bg-muted/30 border border-border space-y-2">
            {asA && (
              <p className="text-sm">
                <span className="text-muted-foreground">As a </span>
                <span className="text-foreground">{asA}</span>
              </p>
            )}
            {iWant && (
              <p className="text-sm">
                <span className="text-muted-foreground">I want </span>
                <span className="text-foreground">{iWant}</span>
              </p>
            )}
            {soThat && (
              <p className="text-sm">
                <span className="text-muted-foreground">So that </span>
                <span className="text-foreground">{soThat}</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Story-specific: Acceptance Criteria */}
      {type === 'story' && acceptanceCriteria && (
        <div>
          <SectionHeader title="Acceptance Criteria" />
          <div className="p-2 rounded bg-muted/30 border border-border">
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {acceptanceCriteria}
            </p>
          </div>
        </div>
      )}

      {/* Children Section */}
      {childCount !== undefined && childCount > 0 && (
        <div>
          <SectionHeader title="Children" count={childCount} />
          <div className="p-2 rounded bg-muted/30 border border-border">
            <p className="text-sm text-muted-foreground">
              {childCount} child {childCount === 1 ? 'item' : 'items'}
            </p>
          </div>
        </div>
      )}

      {/* Semantic ID Section */}
      {semanticId && (
        <div>
          <SectionHeader title="Semantic ID" />
          <div className="p-2 rounded bg-muted/50 border border-border">
            <code className="text-xs font-mono text-muted-foreground break-all">
              {semanticId}
            </code>
          </div>
        </div>
      )}

      {/* Parent Package Section */}
      {parentName && (
        <div>
          <SectionHeader title="Parent Package" />
          <div className="p-2 rounded bg-muted/30 border border-border space-y-1">
            <p className="text-sm text-foreground">{parentName}</p>
            {parentSemanticId && (
              <code className="text-xs font-mono text-muted-foreground break-all">
                {parentSemanticId}
              </code>
            )}
          </div>
        </div>
      )}

      {/* Node ID Section */}
      <div>
        <SectionHeader title="Node ID" />
        <div className="p-2 rounded bg-muted/50 border border-border">
          <code className="text-xs font-mono text-muted-foreground break-all">
            {id}
          </code>
        </div>
      </div>

      {/* Type-specific context */}
      <div>
        <SectionHeader title="Context" />
        <div className="p-2 rounded bg-muted/30 border border-border">
          <p className="text-xs text-muted-foreground">
            {type === 'package' && 'Packages are deployable modules that provide capabilities to the system.'}
            {type === 'capability' && 'Capabilities are business functions provided by packages to end users.'}
            {type === 'feature' && 'Features implement specific functionality within a capability.'}
            {type === 'story' && 'Stories describe user scenarios and acceptance criteria for features.'}
            {type === 'system' && 'The root system node containing all packages and capabilities.'}
          </p>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Test Case Details - same visual pattern as arch entities
// ---------------------------------------------------------------------------

function TestCaseDetailsView({
  id,
  label,
  properties,
  data,
}: {
  id: string;
  label: string;
  properties?: Record<string, unknown>;
  data: Record<string, unknown> | null;
}) {
  // Merge graph properties + BFF data (BFF takes precedence)
  const d = { ...properties, ...data };
  const description = (d.description ?? d.user_outcome) as string | undefined;
  const category = (d.category ?? properties?.category) as string | undefined;
  const caseId = (d.case_id ?? properties?.case_id ?? id) as string;
  const semanticId = (d.semantic_id ?? '') as string;
  const userOutcome = d.user_outcome as string | undefined;
  const businessValue = d.business_value as string | undefined;
  const apisTested = (d.apis_tested ?? []) as string[];

  return (
    <div className="p-3 space-y-4">
      {/* Header — same pattern as arch entities */}
      <div className="flex items-start gap-3">
        <div className={cn('p-2 rounded-lg', TYPE_BADGES.test_case)}>
          <FlaskConical className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold truncate">{label}</h3>
          <span className={cn(
            'inline-block mt-1 px-2 py-0.5 rounded text-xs',
            TYPE_BADGES.test_case
          )}>
            {category ?? 'test case'}
          </span>
        </div>
      </div>

      {/* Description */}
      {description && (
        <div>
          <SectionHeader title="Description" />
          <div className="p-2 rounded bg-muted/30 border border-border">
            <p className="text-sm text-foreground whitespace-pre-wrap">{description}</p>
          </div>
        </div>
      )}

      {/* User Outcome */}
      {userOutcome && userOutcome !== description && (
        <div>
          <SectionHeader title="User Outcome" />
          <div className="p-2 rounded bg-muted/30 border border-border">
            <p className="text-sm text-foreground">{userOutcome}</p>
          </div>
        </div>
      )}

      {/* Business Value */}
      {businessValue && (
        <div>
          <SectionHeader title="Business Value" />
          <div className="p-2 rounded bg-muted/30 border border-border">
            <p className="text-sm text-foreground">{businessValue}</p>
          </div>
        </div>
      )}

      {/* APIs Tested */}
      {apisTested.length > 0 && (
        <div>
          <SectionHeader title="APIs Tested" count={apisTested.length} />
          <div className="space-y-1">
            {apisTested.map((path) => (
              <div key={path} className="p-2 rounded bg-muted/30 border border-border">
                <code className="text-xs font-mono text-foreground">{path}</code>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Node ID */}
      <div>
        <SectionHeader title="Node ID" />
        <div className="p-2 rounded bg-muted/50 border border-border">
          <code className="text-xs font-mono text-muted-foreground break-all">
            {semanticId || id}
          </code>
        </div>
      </div>

      {/* Case ID */}
      <div>
        <SectionHeader title="Case ID" />
        <div className="p-2 rounded bg-muted/50 border border-border">
          <code className="text-xs font-mono text-muted-foreground break-all">
            {caseId}
          </code>
        </div>
      </div>

      {/* Context */}
      <div>
        <SectionHeader title="Context" />
        <div className="p-2 rounded bg-muted/30 border border-border">
          <p className="text-xs text-muted-foreground">
            Test cases verify operational integrity of the system via black-box end-to-end testing.
          </p>
        </div>
      </div>
    </div>
  );
}
